import React from "react";
import {
  Drawer, Box, Typography, IconButton, FormControl, InputLabel, Select, MenuItem,
  Stack, Button, Avatar, Paper, Chip
} from "@mui/material";
import { CheckCircleRounded, Close, Edit, TuneRounded } from "@mui/icons-material";

import BrandedImageFallback from "./BrandedImageFallback";
import {
  brandThemeCssVars,
  DEFAULT_BRAND_THEME,
  normalizeBrandTheme,
} from "../themeTokens";
import { toMediaUrl } from "../api";

/**
 * ComboPickerSheet
 * - Shows combo image header
 * - For each combo category:
 *    * When no selection yet OR currently editing => show <Select>
 *    * After selection => compact summary row with "Edit" + price label based on slot priceType/value
 * - Confirm enabled when all categories have a selected product
 *
 * Props:
 *  - open
 *  - combo { id, name, imageSmallUrl, categories: [{id,name,categoryId,subcategoryId,priceType,priceValueC}], fixedItems? }
 *  - productsPool: normalized products list [{id, name, categoryId, subcategoryId, vatId, vatRate, sellable, imageSmallUrl, ...}]
 *  - priceList { priceByProductId }
 *  - effectivePrices: Map|Object of effective product prices (RON) by productId
 *  - promoByProductId: Object map of productId -> promo price (RON)
 *  - promoByComboId:   Object map of comboId   -> promo base price (RON)  👈 NEW
 *  - onClose()
 *  - onConfirm(selectionsMap, details[], meta?: { isComboPromo: boolean })
 */
export default function ComboPickerSheet({
  open,
  combo,
  productsPool,
  priceList,
  effectivePrices,
  promoByProductId,
  promoByComboId, // NEW
  onClose,
  onConfirm,
  t = (key, fallback) => fallback || key,
  brandTheme = DEFAULT_BRAND_THEME,
}) {
  const [selections, setSelections] = React.useState({});
  const [editingCatId, setEditingCatId] = React.useState(null);
  const c = normalizeBrandTheme(brandTheme);

  const byId = React.useMemo(() => {
    const m = new Map();
    (productsPool || []).forEach(p => m.set(p.id, p));
    return m;
  }, [productsPool]);

  const displayName = (productId, fallback) =>
    byId.get(productId)?.name || fallback || t("skipline.product");

  React.useEffect(() => {
    if (open) {
      setSelections({});
      setEditingCatId(null);
    }
  }, [open, combo?.id]);

  if (!open || !combo) return null;

  const money = (n, c = "RON") =>
    typeof n === "number" ? new Intl.NumberFormat("ro-RO",{style:"currency",currency:c}).format(n) : "-";

  /**
   * Price label for an item using slot priceType/value and pricelist:
   *  - type 0: "0 RON (inclus)"
   *  - type 1: price from promoMap > pricelist for productId
   *  - type 2: set value from combo (priceValueC)
   *
   * item: { priceType, productId? (or id), priceValueC? }
   */
  function labelForItem(item, priceList, promoMap) {
    const productId = item.productId ?? item.id;
    if (item.priceType === 0) return t("combo.includedPrice");
    if (item.priceType === 1) {
      const promo = promoMap?.[productId];
      const pl    = priceList?.priceByProductId?.[productId];
      const v = (promo != null) ? promo : pl;
      return money(typeof v === "number" ? v : 0);
    }
    return money((item.priceValueC || 0) / 100);
  }

  function valueForItem(item, priceList, promoMap) {
    const productId = item.productId ?? item.id;
    if (item.priceType === 0) return 0;
    if (item.priceType === 1) {
      const promo = promoMap?.[productId];
      const pl = priceList?.priceByProductId?.[productId];
      const value = promo != null ? promo : pl;

      return typeof value === "number" ? value : 0;
    }

    return (item.priceValueC || 0) / 100;
  }

  // Accept Map OR plain object for effectivePrices
  const hasEffPrice = (prod) => {
    if (!effectivePrices) return false;
    if (typeof effectivePrices.get === "function") {
      return effectivePrices.get(prod.id) != null;
    }
    return effectivePrices[prod.id] != null;
  };

  // Build options per category with filters
  const optionsByCat = (combo.categories || []).map(cat => {
    const opts = (productsPool || [])
      .filter(p => p.status === 2 && p.isForSale === true)
      .filter(p => {
        const okSub = !cat.subcategoryId || String(p.subcategoryId) === String(cat.subcategoryId);
        const okCat = cat.subcategoryId ? true : (!cat.categoryId || String(p.categoryId) === String(cat.categoryId));
        return okCat && okSub;
      })
      .filter(p => {
        // A product can be chosen only if it has a price in promo or pricelist
        return hasEffPrice(p) || (priceList?.priceByProductId?.[p.id] != null) || (promoByProductId?.[p.id] != null);
      });
    return { cat, opts };
  });

  const currentSel = (catId) => selections[catId] || null;

  const handleChange = (catId, productId) => {
    const opts = optionsByCat.find(x => x.cat.id === catId)?.opts || [];
    const prod = opts.find(p => p.id === productId);
    if (prod) {
      setSelections(prev => ({ ...prev, [catId]: prod }));
      // collapse the select for this category after choosing
      setEditingCatId(null);
    }
  };

  const allSelected = (combo.categories || []).every(cat => !!currentSel(cat.id));
  const selectedCount = (combo.categories || []).filter(cat => !!currentSel(cat.id)).length;
  const comboImageUrl = combo.imageSmallUrl
    ? combo.imageSmallUrl
    : combo.comboImage_productCombo?.[0]?.image
      ? toMediaUrl(combo.comboImage_productCombo[0].image)
      : "";
  const baseComboValue = (combo.basePriceC || 0) / 100;
  const fixedItemsTotal = (combo.fixedItems || []).reduce((total, item) => {
    return total + valueForItem(item, priceList, promoByProductId);
  }, 0);
  const selectedOptionsTotal = (combo.categories || []).reduce((total, cat) => {
    const sel = currentSel(cat.id);
    if (!sel) return total;

    return total + valueForItem(
      {
        priceType: cat.priceType,
        productId: sel.id,
        priceValueC: cat.priceValueC || 0,
      },
      priceList,
      promoByProductId
    );
  }, 0);
  const comboTotal = baseComboValue + fixedItemsTotal + selectedOptionsTotal;

  const confirm = () => {
    const details = (combo.categories || []).map(cat => {
      const sel = currentSel(cat.id);
      const prod = sel ? byId.get(sel.id) : null;
      return {
        categoryId: cat.id,
        productId: sel?.id || null,
        priceType: cat.priceType,
        priceValueC: cat.priceValueC,
        vatId: prod?.vatId || sel?.vatId || sel?.vat || null,
        vatRate: prod?.vatRate ?? sel?.vatRate ?? null,
        // 👇 ensure we pass the real product name
        name: prod?.name || sel?.name || "",
      };
    });

    // Determine if this combo is promo (base). This tells skipLine to route to orderComboPromotionEntry_order.
    const isComboPromo = promoByComboId?.[combo.id] != null;

    onConfirm && onConfirm(selections, details, { isComboPromo });
  };

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{
        role: "dialog",
        "aria-modal": true,
        "aria-labelledby": "combo-picker-title",
        sx: {
          ...brandThemeCssVars(c),
          height: { xs: "92dvh", sm: "min(760px, 88dvh)" },
          minHeight: { sm: 560 },
          maxHeight: { xs: "92dvh", sm: "88dvh" },
          width: { xs: "100%", sm: 620, md: 720 },
          maxWidth: "calc(100vw - 32px)",
          mx: { xs: 0, sm: "auto" },
          mb: { xs: 0, sm: 3 },
          borderTopLeftRadius: { xs: 24, sm: 28 },
          borderTopRightRadius: { xs: 24, sm: 28 },
          borderBottomLeftRadius: { xs: 0, sm: 28 },
          borderBottomRightRadius: { xs: 0, sm: 28 },
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          bgcolor: c.background,
          boxShadow: "0 28px 84px rgba(33,20,12,0.24)",
          "@media (prefers-reduced-motion: reduce)": {
            "&, & *": {
              animationDuration: "0.01ms !important",
              transitionDuration: "0.01ms !important",
              scrollBehavior: "auto !important",
            },
          },
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          px: { xs: 1.5, sm: 2 },
          py: { xs: 1.25, sm: 1.5 },
          borderBottom: "1px solid rgba(124,45,18,0.10)",
          background:
            "linear-gradient(135deg, color-mix(in srgb, var(--brand-bg) 96%, white) 0%, color-mix(in srgb, var(--brand-primary) 11%, var(--brand-bg)) 60%, color-mix(in srgb, var(--brand-cta) 9%, var(--brand-bg)) 100%)",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(160deg, color-mix(in srgb, var(--brand-primary) 13%, transparent) 0%, transparent 42%, color-mix(in srgb, var(--brand-cta) 9%, transparent) 100%)",
            pointerEvents: "none",
          }}
        />

        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ position: "relative" }}>
          <Box
            sx={{
              width: { xs: 72, sm: 86 },
              height: { xs: 64, sm: 74 },
              position: "relative",
              borderRadius: 2.5,
              overflow: "hidden",
              bgcolor: c.surface,
              border: "1px solid rgba(124,45,18,0.12)",
              boxShadow: "0 14px 34px rgba(33,20,12,0.12)",
              flexShrink: 0,
            }}
          >
            <BrandedImageFallback
              icon={<TuneRounded />}
              brandTheme={c}
              sx={{ position: "absolute", inset: 0, minHeight: 0, p: 1 }}
            />
            {comboImageUrl ? (
              <Box
                component="img"
                src={comboImageUrl}
                alt={combo.name}
                onError={(event) => {
                  event.currentTarget.style.display = "none";
                }}
                sx={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  p: 0.75,
                  display: "block",
                  bgcolor: "rgba(255,255,255,0.64)",
                }}
              />
            ) : null}
          </Box>

          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Chip
              size="small"
              label={`${selectedCount}/${combo.categories?.length || 0}`}
              aria-label={`${selectedCount}/${combo.categories?.length || 0}`}
              sx={{
                height: 24,
                fontWeight: 900,
                color: c.onPrimary,
                bgcolor: c.primary,
                border: "1px solid rgba(17,17,17,0.08)",
                mb: 0.75,
              }}
            />

            <Typography
              id="combo-picker-title"
              variant="h6"
              title={combo.name}
              sx={{
                fontWeight: 950,
                color: c.text,
                lineHeight: 1.15,
                letterSpacing: 0,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {t("combo.optionsFor").replace("{combo}", combo.name)}
            </Typography>
          </Box>

          <IconButton
            onClick={onClose}
            aria-label={t("common.close")}
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2.25,
              color: c.text,
              bgcolor: "rgba(255,255,255,0.64)",
              border: "1px solid rgba(124,45,18,0.12)",
              flexShrink: 0,
              "&:hover": {
                bgcolor: "rgba(255,255,255,0.92)",
              },
              "&:focus-visible": {
                outline: `3px solid ${c.cta}`,
                outlineOffset: 2,
              },
            }}
          >
            <Close />
          </IconButton>
        </Stack>
      </Box>

      {/* Body */}
      {combo.fixedItems?.length ? (
        <Box component="section" sx={{ px: 2, pt: 1.5 }}>
          <Typography
            variant="subtitle2"
            sx={{
              mb: 0.75,
              color: c.text,
              fontWeight: 900,
            }}
          >
            {t("combo.included")}
          </Typography>
          {combo.fixedItems.map((fi) => (
            <Box
              key={fi.id}
              sx={{
                px:1.25, py:0.85, mb:0.75,
                display:'flex', alignItems:'center', justifyContent:'space-between',
                borderRadius:2.25,
                border:'1px solid',
                borderColor:'rgba(124,45,18,0.10)',
                bgcolor:'rgba(255,255,255,0.74)',
                boxShadow: "0 8px 20px rgba(33,20,12,0.05)",
              }}
            >
              {/* 👇 real product name from catalog */}
              <Typography variant="body2" sx={{ fontWeight:500 }} noWrap>
                {displayName(fi.productId, fi.name)}
              </Typography>

              <Typography variant="caption" color="text.secondary">
                {labelForItem(fi, priceList, promoByProductId)}
              </Typography>
            </Box>
          ))}
        </Box>
      ) : null}

      <Box
        sx={{
          p: 2,
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overscrollBehavior: "contain",
        }}
      >
        {(combo.categories || []).map(cat => {
          const sel = currentSel(cat.id);
          const opts = (optionsByCat.find(x => x.cat.id === cat.id)?.opts || []);
          const isEditing = !sel || editingCatId === cat.id;

          return (
            <Box key={cat.id}>
              {isEditing ? (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.25,
                    minHeight: 80,
                    display: "flex",
                    alignItems: "center",
                    borderRadius: 2.5,
                    borderColor: "rgba(124,45,18,0.14)",
                    bgcolor: "rgba(255,255,255,0.78)",
                    boxShadow: "0 10px 24px rgba(33,20,12,0.05)",
                  }}
                >
                  <FormControl fullWidth size="medium">
                    <InputLabel id={`label-${cat.id}`}>{cat.name}</InputLabel>
                    <Select
                      labelId={`label-${cat.id}`}
                      label={cat.name}
                      value={sel?.id || ""}
                      onChange={(e) => handleChange(cat.id, e.target.value)}
                      MenuProps={{ PaperProps: { style: { maxHeight: 320 } } }}
                      sx={{
                        minHeight: 48,
                        borderRadius: 2.25,
                        bgcolor:
                          "color-mix(in srgb, var(--brand-bg, #fffaf3) 82%, transparent)",
                      }}
                    >
                      {opts.map(p => (
                        <MenuItem key={p.id} value={p.id} sx={{ minHeight: 52 }}>
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ width:'100%', justifyContent:'space-between' }}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth:0 }}>
                              <Avatar
                                variant="rounded"
                                src={p.imageSmallUrl || undefined}
                                imgProps={{ loading: "lazy" }}
                                sx={{ width: 36, height: 36, borderRadius: 2 }}
                              >
                                {p.name?.[0] || "?"}
                              </Avatar>
                              <Typography variant="body2" noWrap sx={{ fontWeight: 800 }}>{p.name}</Typography>
                            </Stack>
                            {/* Price for this option based on slot priceType/value */}
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800, flexShrink: 0 }}>
                              {labelForItem(
                                {
                                  priceType: cat.priceType,
                                  productId: p.id,
                                  priceValueC: cat.priceValueC || 0,
                                },
                                priceList, promoByProductId
                              )}
                            </Typography>
                          </Stack>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Paper>
              ) : (
                <Paper
                  variant="outlined"
                  sx={{
                    px: 1.25, py: 1.1,
                    minHeight: 80,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    borderRadius: 2.5,
                    borderColor: "rgba(15,118,110,0.22)",
                    bgcolor: "rgba(240,253,250,0.58)",
                    boxShadow: "0 10px 24px rgba(33,20,12,0.05)",
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth:0 }}>
                    <CheckCircleRounded sx={{ color: "success.main", flexShrink: 0 }} fontSize="small" />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontWeight: 800 }} noWrap>
                        {cat.name}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 900, color: c.text }} noWrap>
                        {sel?.name}
                      </Typography>
                    </Box>
                  </Stack>

                  {/* 👉 Price label for the already-selected product */}
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="caption" color="text.secondary">
                      {labelForItem(
                        {
                          priceType: cat.priceType,
                          productId: sel?.id,
                          priceValueC: cat.priceValueC || 0,
                        },
                        priceList, promoByProductId
                      )}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => setEditingCatId(cat.id)}
                      aria-label={t("combo.edit")}
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: 2.25,
                        bgcolor: "rgba(255,255,255,0.78)",
                        border: "1px solid rgba(124,45,18,0.10)",
                        "&:focus-visible": {
                          outline: `3px solid ${c.cta}`,
                          outlineOffset: 2,
                        },
                      }}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                  </Stack>
                </Paper>
              )}
            </Box>
          );
        })}

        <Box sx={{ flex: 1 }} />

        <Stack
          direction="row"
          spacing={1}
          sx={{
            position: "sticky",
            bottom: -16,
            mx: -2,
            mb: -2,
            px: 2,
            py: 1.25,
            pb: "max(10px, env(safe-area-inset-bottom))",
            bgcolor:
              "color-mix(in srgb, var(--brand-bg, #fffaf3) 96%, transparent)",
            backdropFilter: "blur(16px)",
            borderTop: "1px solid rgba(124,45,18,0.10)",
            flexWrap: "wrap",
            rowGap: 1,
          }}
        >
          <Button
            onClick={onClose}
            sx={{
              minHeight: 44,
              borderRadius: 2.25,
              fontWeight: 900,
              textTransform: "none",
              color: c.text,
              "&:focus-visible": {
                outline: `3px solid ${c.cta}`,
                outlineOffset: 2,
              },
            }}
          >
            {t("combo.back")}
          </Button>
          <Box sx={{ flex: 1 }} />

          <Box
            sx={{
              minWidth: 0,
              textAlign: "right",
              alignSelf: "center",
              mr: { xs: 0.5, sm: 1 },
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: "block",
                fontWeight: 800,
                lineHeight: 1.1,
              }}
            >
              {t("combo.total")}
            </Typography>

            <Typography
              sx={{
                color: c.text,
                fontWeight: 950,
                lineHeight: 1.15,
                whiteSpace: "nowrap",
              }}
            >
              {money(comboTotal, priceList?.currency || "RON")}
            </Typography>
          </Box>

          <Button
            variant="contained"
            onClick={confirm}
            disabled={!allSelected}
            sx={{
              minHeight: 48,
              width: { xs: "100%", sm: "auto" },
              borderRadius: 2.25,
              fontWeight: 950,
              textTransform: "none",
              px: 2.25,
              boxShadow: "0 12px 28px rgba(247,181,0,0.18)",
              "&:focus-visible": {
                outline: `3px solid ${c.cta}`,
                outlineOffset: 2,
              },
            }}
          >
            {t("combo.addToCart")}
          </Button>
        </Stack>
      </Box>
    </Drawer>
  );
}
